import { useMemo, useState } from 'react';

import { SearchInput } from '../ui/Field';
import { Icon } from '../ui/Icon';

/**
 * "Who has this?" — the one list behind every grant/revoke screen.
 *
 * Assigning managers to a fleet and granting a viewer access to vehicles are
 * the same interaction wearing different nouns: a searchable list of
 * candidates, the ones already assigned pinned to the top, and a single button
 * per row that adds or removes. Writing it twice would mean two different
 * answers to "what happens while the request is in flight", which is exactly
 * the sort of drift that makes an access screen untrustworthy.
 *
 * The caller owns the data and the mutation. This owns the search box, the
 * ordering, and the in-flight row — nothing else.
 *
 *   <AssignmentList
 *     items={vehicles}
 *     assignedIds={granted}
 *     onToggle={(item, next) => grant(item.id, next)}
 *     primary={(v) => v.reg_number}
 *     secondary={(v) => v.fleet_name}
 *   />
 */
export function AssignmentList({
  items = [],
  assignedIds,
  onToggle,
  pendingId = null,
  primary,
  secondary,
  searchPlaceholder = 'Search',
  searchKeys,
  emptyLabel = 'Nothing to show',
  addLabel = 'Add',
  removeLabel = 'Remove',
  height = 'max-h-[19rem]',
}) {
  const [query, setQuery] = useState('');
  const assigned = assignedIds instanceof Set ? assignedIds : new Set(assignedIds || []);

  const ordered = useMemo(() => {
    const needle = query.trim().toLowerCase();

    const matches = (item) => {
      if (!needle) return true;
      const haystack = searchKeys
        ? searchKeys.map((key) => item[key]).join(' ')
        : `${primary(item) ?? ''} ${secondary?.(item) ?? ''}`;
      return String(haystack).toLowerCase().includes(needle);
    };

    // Assigned first. Someone opening this screen is usually checking what is
    // already granted, not scrolling for it.
    return items
      .filter(matches)
      .slice()
      .sort((a, b) => {
        const byState = Number(assigned.has(b.id)) - Number(assigned.has(a.id));
        if (byState) return byState;
        return String(primary(a) ?? '').localeCompare(String(primary(b) ?? ''));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, query, assignedIds]);

  return (
    <div className="space-y-2">
      {items.length > 6 && (
        <SearchInput value={query} onChange={setQuery} placeholder={searchPlaceholder} />
      )}

      <div className={`${height} overflow-y-auto rounded-lg border border-line`}>
        {ordered.length === 0 ? (
          <p className="px-3 py-6 text-center text-[12.5px] text-ink-3">
            {query ? `Nothing matches “${query}”.` : emptyLabel}
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {ordered.map((item) => {
              const isAssigned = assigned.has(item.id);
              const busy = pendingId === item.id;

              return (
                <li key={item.id} className="flex items-center gap-3 px-3 py-2">
                  <span
                    className={`flex size-5 shrink-0 items-center justify-center rounded-full ${
                      isAssigned ? 'bg-good-soft text-good-ink' : 'bg-surface-2 text-ink-3'
                    }`}
                  >
                    <Icon name={isAssigned ? 'check' : 'plus'} size="xs" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-medium text-ink">{primary(item)}</p>
                    {secondary && (
                      <p className="truncate text-[12px] text-ink-3">{secondary(item) || '—'}</p>
                    )}
                  </div>

                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onToggle(item, !isAssigned)}
                    className={`shrink-0 rounded-md px-2 py-1 text-[12.5px] font-medium disabled:opacity-50 ${
                      isAssigned
                        ? 'text-critical-ink hover:bg-critical-soft'
                        : 'text-brand hover:bg-brand-soft'
                    }`}
                  >
                    {busy ? '…' : isAssigned ? removeLabel : addLabel}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
